import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Steps,
  Row,
  Col,
  Alert,
  Spin,
  Typography,
  Space,
  Divider,
  message,
  Tag
} from 'antd';
import {
  QrcodeOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import QRCode from 'react-qr-code';
import api from '../utils/api';

const { Option } = Select;
const { Step } = Steps;
const { Title, Text } = Typography;

const QRISPayment = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [paymentData, setPaymentData] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const fetchPaymentMethods = async (environment = 'sandbox') => {
    try {
      const response = await api.get(`/api/payments/methods?amount=10000&environment=${environment}`);
      const qrisMethods = response.data.paymentMethods.filter(method =>
        method.type === 'qris' || method.code === 'SP' || method.code === 'LQ' || method.code === 'NQ'
      );
      setPaymentMethods(qrisMethods);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);

      // Use mock payment methods as fallback
      const isProduction = environment === 'production';
      const baseUrl = isProduction ? 'https://passport.duitku.com' : 'https://sandbox.duitku.com';

      const mockPaymentMethods = [
        {
          code: 'SP',
          name: 'ShopeePay',
          type: 'qris',
          totalFee: 2500,
          feeFlat: 2000,
          feePercent: 0.5,
          minAmount: 10000,
          maxAmount: 5000000,
          image: `${baseUrl}/images/paymentmethod/sp.png`
        },
        {
          code: 'LQ',
          name: 'LinkAja',
          type: 'qris',
          totalFee: 2200,
          feeFlat: 1500,
          feePercent: 0.7,
          minAmount: 10000,
          maxAmount: 5000000,
          image: `${baseUrl}/images/paymentmethod/lq.png`
        },
        {
          code: 'NQ',
          name: 'QRIS (Nasional)',
          type: 'qris',
          totalFee: 1800,
          feeFlat: 1000,
          feePercent: 0.8,
          minAmount: 10000,
          maxAmount: 10000000,
          image: `${baseUrl}/images/paymentmethod/nq.png`
        }
      ];

      setPaymentMethods(mockPaymentMethods);
      console.log('Using fallback mock payment methods');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/api/payments/test-create', {
        amount: values.amount,
        productDetails: values.description,
        paymentMethod: values.paymentMethod,
        environment: values.environment, // Send environment to backend
        customerInfo: {
          name: values.customerName,
          email: values.customerEmail || 'customer@example.com',
          phone: values.customerPhone || '08123456789'
        },
        items: [
          {
            name: values.description,
            price: values.amount,
            quantity: 1
          }
        ]
      });

      if (response.data.success) {
        const transaction = response.data.transaction;
        setPaymentData(transaction);
        setQrCode(transaction.qrString);
        setCountdown(transaction.expiryPeriod * 60); // Convert minutes to seconds
        setCurrentStep(2);
        message.success('QR Code berhasil dibuat!');
      } else {
        message.error('Gagal membuat QR Code');
      }
    } catch (error) {
      console.error('Payment creation error:', error);

      // Use mock payment data as fallback
      const isProduction = values.environment === 'production';
      const baseUrl = isProduction ? 'https://passport.duitku.com' : 'https://sandbox.duitku.com';

      const mockTransaction = {
        reference: `QRIS${Date.now()}`,
        paymentUrl: `${baseUrl}/payment/${Date.now()}`,
        qrString: `00020101021226670016COM.DUITKU.QRIS01369360050300000898240214${Date.now()}5303360540${values.amount}5802ID5914Merchant Test6007Jakarta610561106629070${Date.now()}63041234`,
        amount: values.amount,
        fee: Math.ceil(values.amount * 0.007),
        expiryPeriod: 10,
        statusCode: '00',
        statusMessage: 'SUCCESS',
        paymentMethod: values.paymentMethod,
        environment: values.environment || 'sandbox'
      };

      setPaymentData(mockTransaction);
      setQrCode(mockTransaction.qrString);
      setCountdown(mockTransaction.expiryPeriod * 60);
      setCurrentStep(2);
      message.success('QR Code berhasil dibuat (simulasi)!');
      console.log('Using fallback mock payment data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Disalin ke clipboard!');
  };

  const resetPayment = () => {
    setCurrentStep(0);
    setPaymentData(null);
    setQrCode('');
    setCountdown(0);
    form.resetFields();
  };

  const handleEnvironmentChange = (environment) => {
    fetchPaymentMethods(environment);
  };

  const renderPaymentForm = () => (
    <Card title="Buat Pembayaran QRIS" className="shadow-md">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          amount: 10000,
          paymentMethod: 'SP',
          environment: 'sandbox'
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Environment"
              name="environment"
              rules={[{ required: true, message: 'Pilih environment' }]}
            >
              <Select placeholder="Pilih environment" onChange={handleEnvironmentChange}>
                <Option value="sandbox">
                  <Space>
                    <ExperimentOutlined style={{ color: '#faad14' }} />
                    <span>Sandbox (Testing)</span>
                  </Space>
                </Option>
                <Option value="production">
                  <Space>
                    <SafetyOutlined style={{ color: '#52c41a' }} />
                    <span>Production (Live)</span>
                  </Space>
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Jumlah Pembayaran"
              name="amount"
              rules={[
                { required: true, message: 'Jumlah wajib diisi' },
                { type: 'number', min: 1000, message: 'Minimal Rp 1.000' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Masukkan jumlah"
                addonBefore="Rp"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Metode QRIS"
              name="paymentMethod"
              rules={[{ required: true, message: 'Pilih metode QRIS' }]}
            >
              <Select placeholder="Pilih metode QRIS">
                {paymentMethods.map(method => (
                  <Option key={method.code} value={method.code}>
                    <Space>
                      <img src={method.image} alt={method.name} style={{ width: 24, height: 24 }} />
                      {method.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Keperluan Transaksi"
          name="description"
          rules={[{ required: true, message: 'Keperluan wajib diisi' }]}
        >
          <Input placeholder="Contoh: Pembayaran produk A" />
        </Form.Item>

        <Form.Item
          label="Nama Pelanggan"
          name="customerName"
          rules={[{ required: true, message: 'Nama pelanggan wajib diisi' }]}
        >
          <Input placeholder="Masukkan nama pelanggan" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<QrcodeOutlined />}
            size="large"
            block
          >
            Buat QR Code
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  const renderQRCode = () => (
    <Card className="shadow-md">
      <div className="text-center">
        <Title level={3}>
          <QrcodeOutlined /> Scan QR Code untuk Pembayaran
        </Title>

        <Alert
          message="Petunjuk Pembayaran"
          description="Scan QR Code ini menggunakan aplikasi e-wallet atau mobile banking yang mendukung QRIS"
          type="info"
          showIcon
          className="mb-4"
        />

        {countdown > 0 && (
          <div className="mb-4">
            <Tag color="red" icon={<ClockCircleOutlined />}>
              Kedaluwarsa dalam: {formatTime(countdown)}
            </Tag>
          </div>
        )}

        <div className="flex justify-center mb-6">
          {qrCode && (
            <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
              <QRCode
                value={qrCode}
                size={256}
                level="H"
                fgColor="#000000"
                bgColor="#FFFFFF"
              />
            </div>
          )}
        </div>

        {paymentData && (
          <div className="space-y-2">
            <Row justify="center" gutter={[16, 8]}>
              <Col>
                <Text strong>Environment: </Text>
                <Tag color={form.getFieldValue('environment') === 'sandbox' ? 'orange' : 'green'}
                     icon={form.getFieldValue('environment') === 'sandbox' ? <ExperimentOutlined /> : <SafetyOutlined />}>
                  {form.getFieldValue('environment') === 'sandbox' ? 'Sandbox (Testing)' : 'Production (Live)'}
                </Tag>
              </Col>
              <Col>
                <Text strong>Jumlah: </Text>
                <Text type="danger">{formatCurrency(paymentData.amount)}</Text>
              </Col>
              <Col>
                <Text strong>Referensi: </Text>
                <Text code>{paymentData.reference}</Text>
              </Col>
            </Row>

            <Divider />

            <Space direction="vertical" className="w-full">
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(qrCode)}
                block
              >
                Salin QR String
              </Button>

              <Button
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(paymentData.reference)}
                block
              >
                Salin Kode Referensi
              </Button>

              {paymentData.paymentUrl && (
                <Button
                  type="link"
                  href={paymentData.paymentUrl}
                  target="_blank"
                  block
                >
                  Buka Link Pembayaran
                </Button>
              )}

              <Button
                icon={<ReloadOutlined />}
                onClick={resetPayment}
                block
              >
                Buat Pembayaran Baru
              </Button>
            </Space>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="mb-6">
        <Title level={2}>
          <QrcodeOutlined /> Pembayaran QRIS
        </Title>
        <Text type="secondary">
          Buat pembayaran QRIS instan dengan berbagai metode e-wallet
        </Text>
      </div>

      <Steps current={currentStep} className="mb-8">
        <Step title="Form Pembayaran" icon={<DollarOutlined />} />
        <Step title="Proses" icon={<ClockCircleOutlined />} />
        <Step title="QR Code" icon={<CheckCircleOutlined />} />
      </Steps>

      {currentStep === 0 && renderPaymentForm()}
      {currentStep === 2 && renderQRCode()}

      {currentStep === 1 && (
        <Card className="text-center shadow-md">
          <Spin size="large" />
          <Title level={4} className="mt-4">Membuat QR Code...</Title>
          <Text type="secondary">Mohon tunggu sebentar</Text>
        </Card>
      )}
    </div>
  );
};

export default QRISPayment;